import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Target, Loader2, Search } from "lucide-react";

const TARGETS = [
  { key: "corner", label: "Magasin (corner)", roles: ["corner"] },
  { key: "mm",     label: "Manager Multi-sites (MM)", roles: ["mm"] },
  { key: "rt",     label: "Formateur (RT)", roles: ["rt"] },
  { key: "bm",     label: "Business Manager (BM)", roles: ["manager", "controller"] },
];

const PRIORITIES = [
  { key: "normal",    label: "Normal",    color: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "important", label: "Important", color: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "urgent",    label: "Urgent",    color: "bg-rose-100 text-rose-800 border-rose-300" },
];

export default function MissionAffecteeModal({ open, onClose, onSaved, prefill = null }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [deadline, setDeadline] = useState("");
  const [hour, setHour] = useState("");
  const [targetType, setTargetType] = useState("corner");
  const [targetId, setTargetId] = useState("");
  const [saving, setSaving] = useState(false);

  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [storeQ, setStoreQ] = useState("");
  const [userQ, setUserQ] = useState("");
  const [loadingList, setLoadingList] = useState(false);

  const currentTarget = useMemo(() => TARGETS.find(t => t.key === targetType), [targetType]);

  useEffect(() => {
    if (!open) return;
    setLoadingList(true);
    Promise.all([
      api.get("/corner/stores-search", { params: { q: "", limit: 500 } }).catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
    ]).then(([s, u]) => {
      setStores(s.data || []);
      setUsers(u.data || []);
    }).finally(() => setLoadingList(false));
  }, [open]);

  useEffect(() => {
    // Reset target selection when switching target type
    setTargetId("");
    setStoreQ(""); setUserQ("");
  }, [targetType]);

  // Pré-remplissage (ex : mission en réponse à un ticket support) — appliqué à l'ouverture uniquement
  useEffect(() => {
    if (!open || !prefill) return;
    setTitle(prefill.title || ""); setDescription(prefill.description || "");
    setPriority(prefill.priority || "normal");
    if (prefill.targetType) setTargetType(prefill.targetType);
    const t = setTimeout(() => { if (prefill.targetId) setTargetId(prefill.targetId); }, 0);
    return () => clearTimeout(t);
  }, [open, prefill]);

  const filteredStores = useMemo(() => {
    if (!storeQ.trim()) return stores.slice(0, 50);
    const q = storeQ.toLowerCase();
    return stores.filter(s => (s.name || "").toLowerCase().includes(q) ||
                              (s.canonical_name || "").toLowerCase().includes(q)).slice(0, 50);
  }, [stores, storeQ]);

  const filteredUsers = useMemo(() => {
    const roles = currentTarget?.roles || [];
    let base = users.filter(u => roles.includes(u.role));
    const pre = prefill?.targetId && users.find(u => u.id === prefill.targetId);
    if (pre && !base.some(u => u.id === pre.id)) base = [pre, ...base];
    if (!userQ.trim()) return base.slice(0, 50);
    const q = userQ.toLowerCase();
    return base.filter(u => (u.name || "").toLowerCase().includes(q) ||
                             (u.email || "").toLowerCase().includes(q)).slice(0, 50);
  }, [users, userQ, currentTarget, prefill]);

  const reset = () => {
    setTitle(""); setDescription(""); setPriority("normal");
    setDeadline(""); setHour(""); setTargetType("corner"); setTargetId("");
    setStoreQ(""); setUserQ("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return toast.error("Titre obligatoire");
    if (!targetId) return toast.error("Destinataire obligatoire");
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        priority,
        deadline: deadline || null,
        hour: hour || null,
        target_type: targetType === "corner" ? "corner" : "user",
      };
      if (targetType === "corner") payload.target_store_id = targetId;
      else payload.target_user_id = targetId;
      if (prefill?.support_ticket_id) payload.support_ticket_id = prefill.support_ticket_id;
      await api.post("/plans-action/mission-affectee", payload);
      toast.success("Mission affectée ✓");
      reset(); onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] grid place-items-center bg-black/40 p-4" data-testid="mission-modal">
      <form onSubmit={submit}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 text-white grid place-items-center"><Target className="w-4 h-4"/></div>
            <div>
              <div className="font-display font-bold text-slate-900">Affecter une mission ou action</div>
              <div className="text-[11px] text-slate-500">La tâche apparaîtra dans le tableau de bord du destinataire.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="mission-modal-close"
                  className="w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Titre */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Titre de la mission *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
                   data-testid="mission-title"
                   placeholder="Ex. Vérifier les DLC rayon frais"
                   className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"/>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Description (optionnel)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
                      data-testid="mission-desc"
                      rows={2} placeholder="Contexte, points de vigilance, etc."
                      className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"/>
          </div>

          {/* Priorité */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Priorité</label>
            <div className="grid grid-cols-3 gap-2">
              {PRIORITIES.map(p => {
                const active = priority === p.key;
                return (
                  <button key={p.key} type="button" onClick={() => setPriority(p.key)}
                          data-testid={`mission-priority-${p.key}`}
                          className={`p-2 rounded-lg border-2 font-semibold text-xs transition ${active ? p.color : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Deadline + Heure */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Date d'échéance</label>
              <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
                     data-testid="mission-deadline"
                     className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm"/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Heure (optionnel)</label>
              <input type="time" value={hour} onChange={e => setHour(e.target.value)}
                     data-testid="mission-hour"
                     className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm"/>
            </div>
          </div>

          {/* Type de destinataire */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Type de destinataire</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TARGETS.map(t => {
                const active = targetType === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => setTargetType(t.key)}
                          data-testid={`mission-target-type-${t.key}`}
                          className={`p-2.5 rounded-lg border-2 font-semibold text-xs transition ${active ? "border-blue-500 bg-blue-50 text-blue-700" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Destinataire sélecteur */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
              {targetType === "corner" ? "Magasin destinataire *" : `${currentTarget?.label} destinataire *`}
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={targetType === "corner" ? storeQ : userQ}
                     onChange={e => targetType === "corner" ? setStoreQ(e.target.value) : setUserQ(e.target.value)}
                     data-testid="mission-target-search"
                     placeholder={targetType === "corner" ? "Rechercher un magasin…" : "Rechercher une personne…"}
                     className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm"/>
            </div>
            <div className="mt-2 max-h-48 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
              {loadingList && <div className="p-4 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline"/></div>}
              {!loadingList && targetType === "corner" && filteredStores.map(s => (
                <button key={s.id} type="button" onClick={() => setTargetId(s.id)}
                        data-testid={`mission-target-${s.id}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${targetId === s.id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                  <div className="w-6 h-6 rounded-full bg-slate-100 grid place-items-center text-slate-700 text-[10px] font-bold">{(s.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{s.enseigne} · {s.sector_zone || s.region_name || "—"}</div>
                  </div>
                  {targetId === s.id && <span className="text-blue-600 text-lg">✓</span>}
                </button>
              ))}
              {!loadingList && targetType !== "corner" && filteredUsers.map(u => (
                <button key={u.id} type="button" onClick={() => setTargetId(u.id)}
                        data-testid={`mission-target-${u.id}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${targetId === u.id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                  <div className="w-6 h-6 rounded-full bg-slate-100 grid place-items-center text-slate-700 text-[10px] font-bold">{(u.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{u.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                  </div>
                  {targetId === u.id && <span className="text-blue-600 text-lg">✓</span>}
                </button>
              ))}
              {!loadingList && ((targetType === "corner" ? filteredStores : filteredUsers).length === 0) && (
                <div className="p-4 text-center text-xs text-slate-400 italic">Aucun résultat</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-slate-100 bg-slate-50">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200">
            Annuler
          </button>
          <button type="submit" disabled={saving || !title.trim() || !targetId}
                  data-testid="mission-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm disabled:opacity-50 shadow">
            {saving && <Loader2 className="w-4 h-4 animate-spin"/>}
            Affecter la mission
          </button>
        </div>
      </form>
    </div>
  );
}
